import { useCallback, useRef, useEffect } from 'react';

interface UseNotificationSoundReturn {
  playNotificationSound: () => void;
}

/**
 * Hook simple para reproducir sonido de notificación
 * 
 * @example
 * ```tsx
 * const { playNotificationSound } = useNotificationSound();
 * 
 * // Reproducir sonido
 * playNotificationSound();
 * ```
 */
export const useNotificationSound = (): UseNotificationSoundReturn => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Inicializar el audio
  useEffect(() => {
    if (typeof window !== 'undefined') {
      audioRef.current = new Audio('/sounds/notification.mp3');
      audioRef.current.volume = 0.7;
      audioRef.current.preload = 'auto';
      
      // Manejar errores de carga
      audioRef.current.addEventListener('error', (e) => {
        console.warn('Error al cargar el sonido de notificación:', e);
      });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('error', () => {});
      }
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    if (!audioRef.current) {
      return;
    }

    try {
      // Resetear el audio al inicio para permitir reproducción múltiple
      audioRef.current.currentTime = 0;
      
      // Reproducir el sonido
      const playPromise = audioRef.current.play();
      
      // Manejar promesas de reproducción (algunos navegadores requieren interacción del usuario)
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            if (import.meta.env.DEV) {
              console.log('🔊 Sonido de notificación reproducido');
            }
          })
          .catch((error) => {
            // Silenciar errores de autoplay (común en navegadores modernos)
            if (error.name !== 'NotAllowedError') {
              console.warn('Error al reproducir sonido de notificación:', error);
            }
          });
      }
    } catch (error) {
      console.warn('Error al reproducir sonido de notificación:', error);
    }
  }, []);

  return {
    playNotificationSound,
  };
};
